import { Link, useSearch } from '@tanstack/react-router';
import {
  ArrowRightLeft,
  BarChart3,
  Bot,
  Building2,
  DollarSign,
  Files,
  FileText,
  FlaskConical,
  Hash,
  ListOrdered,
  MessageSquare,
  Megaphone,
  Play,
  ScanSearch,
  Send,
  Sparkles,
  Tags,
  ThumbsUp,
  UserMinus,
  Users,
  Vote,
} from 'lucide-react';

import { useAnalysisAccess } from '@/hooks/useAnalysis';
import {
  ANALYSIS_SEARCH_DEFAULTS,
  USERS_SEARCH_DEFAULTS,
} from '@/lib/route-search';
import { cn } from '@/lib/utils';

import type { ReactNode } from 'react';

type SidebarProps = {
  id?: string;
  className?: string;
  onNavigate?: () => void;
};

const NAV_LINK_PROPS = {
  className:
    'flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors',
  activeProps: { className: 'bg-primary text-primary-foreground' },
  inactiveProps: {
    className: 'text-muted-foreground hover:text-foreground hover:bg-muted',
  },
};

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2 pb-3">
      <p className="px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

export default function Sidebar({ id, className, onNavigate }: SidebarProps) {
  const { data: canSeeAnalysis = false } = useAnalysisAccess();
  const activeUsersSearch = useSearch({
    from: '/users',
    shouldThrow: false,
  });
  const usersReturnTo = useSearch({
    from: '/users/$userId',
    shouldThrow: false,
    select: (search) => search.returnTo,
  });
  const usersSearch =
    activeUsersSearch ?? usersReturnTo ?? USERS_SEARCH_DEFAULTS;
  const activeAnalysisSearch = useSearch({
    from: '/analysis',
    shouldThrow: false,
  });
  const newAnalysisReturnTo = useSearch({
    from: '/analysis/new',
    shouldThrow: false,
    select: (search) => search.returnTo,
  });
  const analysisDetailReturnTo = useSearch({
    from: '/analysis/$runId',
    shouldThrow: false,
    select: (search) => search.returnTo,
  });
  const analysisSearch =
    activeAnalysisSearch ??
    newAnalysisReturnTo ??
    analysisDetailReturnTo ??
    ANALYSIS_SEARCH_DEFAULTS;

  return (
    <div
      id={id}
      className={cn(
        'h-full w-64 shrink-0 overflow-y-auto border-r bg-card',
        className
      )}
    >
      <div className="p-6">
        <h2 className="text-lg font-semibold mb-6">Navigation</h2>
        <nav className="space-y-2" onClick={onNavigate}>
          <NavGroup label="Accounts">
            <Link to="/users" search={usersSearch} {...NAV_LINK_PROPS}>
              <Users className="h-4 w-4 mr-2" />
              Users
            </Link>

            <Link to="/teams" {...NAV_LINK_PROPS}>
              <Building2 className="h-4 w-4 mr-2" />
              Teams
            </Link>
          </NavGroup>

          <NavGroup label="Quality">
            <Link to="/feedback" {...NAV_LINK_PROPS}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Feedback
            </Link>

            <Link to="/message-votes" {...NAV_LINK_PROPS}>
              <ThumbsUp className="h-4 w-4 mr-2" />
              Message Votes
            </Link>

            <Link to="/vote-aggregates" {...NAV_LINK_PROPS}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Vote Aggregates
            </Link>

            <Link to="/entity-votes" {...NAV_LINK_PROPS}>
              <Vote className="h-4 w-4 mr-2" />
              Other Votes
            </Link>

            <Link to="/eval-cases" {...NAV_LINK_PROPS}>
              <Files className="h-4 w-4 mr-2" />
              Eval cases
            </Link>

            <div className="pt-3">
              <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Online eval
              </p>
              <div className="space-y-2">
                <Link to="/online-eval/issues" {...NAV_LINK_PROPS}>
                  <ScanSearch className="mr-2 h-4 w-4" />
                  Flagged issues
                </Link>
                <Link to="/online-eval/categories" {...NAV_LINK_PROPS}>
                  <Tags className="mr-2 h-4 w-4" />
                  Issue categories
                </Link>
                <Link to="/online-eval/ad-hoc-runs" {...NAV_LINK_PROPS}>
                  <Play className="mr-2 h-4 w-4" />
                  Ad hoc runs
                </Link>
              </div>
            </div>
          </NavGroup>

          <NavGroup label="Agents & experiments">
            <Link
              to="/proactive-routines"
              activeOptions={{ includeSearch: false }}
              {...NAV_LINK_PROPS}
            >
              <Bot className="h-4 w-4 mr-2" />
              Proactive Routines
            </Link>

            <Link to="/proactive-agents" {...NAV_LINK_PROPS}>
              <Send className="h-4 w-4 mr-2" />
              Proactive Agents
            </Link>

            <Link to="/proactivity-experiments" {...NAV_LINK_PROPS}>
              <Sparkles className="h-4 w-4 mr-2" />
              Proactivity Experiments
            </Link>

            <Link to="/agent-configs" {...NAV_LINK_PROPS}>
              <Bot className="h-4 w-4 mr-2" />
              Agent Configs
            </Link>

            <Link to="/prompts" {...NAV_LINK_PROPS}>
              <FileText className="h-4 w-4 mr-2" />
              Prompts
            </Link>

            <Link
              to="/broadcasts"
              activeOptions={{ includeSearch: false }}
              {...NAV_LINK_PROPS}
            >
              <Megaphone className="h-4 w-4 mr-2" />
              Broadcasts
            </Link>
          </NavGroup>

          <NavGroup label="Insights">
            {canSeeAnalysis && (
              <Link to="/analysis" search={analysisSearch} {...NAV_LINK_PROPS}>
                <FlaskConical className="h-4 w-4 mr-2" />
                Analysis
              </Link>
            )}

            <Link to="/churn-analysis" {...NAV_LINK_PROPS}>
              <UserMinus className="h-4 w-4 mr-2" />
              Churn Analysis
            </Link>

            <Link to="/action-card-feed" {...NAV_LINK_PROPS}>
              <ListOrdered className="h-4 w-4 mr-2" />
              Action Card Feed
            </Link>

            <Link to="/cost-analytics" {...NAV_LINK_PROPS}>
              <DollarSign className="h-4 w-4 mr-2" />
              Cost Analytics
            </Link>
          </NavGroup>

          <NavGroup label="Slack bot">
            <Link to="/slack-bot" {...NAV_LINK_PROPS}>
              <Hash className="h-4 w-4 mr-2" />
              Spend and health
            </Link>
          </NavGroup>

          <NavGroup label="Operations">
            <Link to="/cutover" {...NAV_LINK_PROPS}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Backend cutover
            </Link>
          </NavGroup>
        </nav>
      </div>
    </div>
  );
}
